import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import { printTicket80mm } from '../utils/ticket80mmPrint';
import PageHeader from '../components/layout/PageHeader';
import HistoriqueVenteModals from '../components/historique/HistoriqueVenteModals';
import HistoriqueSumUpPendingPanel from '../components/historique/HistoriqueSumUpPendingPanel';
import HistoriqueVentesList from '../components/historique/HistoriqueVentesList';
import HistoriqueVentesActionsBar from '../components/historique/HistoriqueVentesActionsBar';
import useHistoriqueVenteActions from '../hooks/historique/useHistoriqueVenteActions';
import { activiteVentesLinkFromFilters } from '../utils/historique/ventesDisplay';
import { isPrimaryAdmin } from '../utils/roleRouting';
import { History, Search, Filter, ChevronDown, ChevronUp, Activity } from 'lucide-react';

const HistoriquePage = () => {
  const { api, isAdmin, user } = useAuth();
  const isCarlosOnly = isPrimaryAdmin(user);
  const { t, tError, formatCurrency } = useLanguage();
  const [searchParams] = useSearchParams();
  const [ventes, setVentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState(() => ({
    vendeur: searchParams.get('vendeur') || '',
    date_debut: searchParams.get('date_debut') || '',
    date_fin: searchParams.get('date_fin') || '',
    search: searchParams.get('search') || '',
    magasin_id: searchParams.get('magasin_id') || '',
  }));
  const [totaux, setTotaux] = useState({ ventes: 0, acomptes: 0, retraits: 0, global: 0 });
  const [commissions, setCommissions] = useState(null);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const fetchHistorique = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: currentPage, limit: 20 });
      if (filters.vendeur) params.append('vendeur', filters.vendeur);
      if (filters.date_debut) params.append('date_debut', filters.date_debut);
      if (filters.date_fin) params.append('date_fin', filters.date_fin);
      if (filters.search) params.append('search', filters.search);
      if (filters.magasin_id) params.append('magasin_id', filters.magasin_id);

      const response = await api.get(`/ventes/historique?${params.toString()}`);
      setVentes(response.data.ventes);
      setTotalPages(response.data.total_pages);
      if (response.data.totaux) {
        setTotaux(response.data.totaux);
      }
      if (filters.vendeur && filters.date_debut && filters.date_fin) {
        await fetchCommissions();
      } else {
        setCommissions(null);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCommissions = async () => {
    try {
      const response = await api.get(`/ventes/commissions`, {
        params: {
          vendeur: filters.vendeur,
          date_debut: filters.date_debut,
          date_fin: filters.date_fin
        }
      });
      setCommissions(response.data);
    } catch (error) {
      console.error('Error commissions:', error);
    }
  };

  const openPdfForPrint = async (endpoint) => {
    try {
      const response = await api.get(endpoint, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank', 'width=900,height=700');
      if (!printWindow) {
        alert(t('historique.popupsAutoriser'));
        return;
      }
      const triggerPrint = () => {
        printWindow.focus();
        printWindow.print();
      };
      printWindow.addEventListener('load', triggerPrint);
      setTimeout(triggerPrint, 1200);
      setTimeout(() => window.URL.revokeObjectURL(url), 8000);
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('historique.erreurTelechargement'));
    }
  };

  const fetchPendingPayments = useCallback(async () => {
    try {
      const response = await api.get('/payments/sumup/pending');
      setPendingPayments(response.data || []);
    } catch (error) {
      console.error('Erreur paiements SumUp:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchHistorique();
    fetchPendingPayments();
  }, [currentPage]);

  const onRefresh = useCallback(() => {
    fetchHistorique();
    fetchPendingPayments();
  }, [fetchPendingPayments]);

  const { convertingRef, actionHandlers, modals } = useHistoriqueVenteActions({
    api,
    onRefresh,
    testIdPrefix: 'historique',
  });

  const handleRapportVendeur = async () => {
    if (!filters.vendeur || !filters.date_debut || !filters.date_fin) {
      alert(t('historique.vendeurDatesRequis'));
      return;
    }
    try {
      await printTicket80mm(api, {
        type: 'rapport_vendeur',
        vendeur: filters.vendeur,
        dateDebut: filters.date_debut,
        dateFin: filters.date_fin,
      });
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('historique.erreurImpressionRapport'));
    }
  };

  const handleConfirmSumupPayment = async (checkoutId) => {
    try {
      await api.post(`/payments/sumup/staff-confirm/${checkoutId}`);
      onRefresh();
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('historique.erreurConfirmationSumup'));
    }
  };

  const handleCancelSumupPayment = async (checkoutId) => {
    if (!window.confirm(t('historique.confirmRefusPaiement'))) return;
    try {
      await api.post(`/payments/sumup/cancel/${checkoutId}`);
      onRefresh();
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('historique.erreurRefusSumup'));
    }
  };

  const applyFilters = () => {
    setCurrentPage(1);
    fetchHistorique();
  };

  const resetFilters = () => {
    setFilters({ vendeur: '', date_debut: '', date_fin: '', search: '', magasin_id: '' });
    setCurrentPage(1);
    fetchHistorique();
  };

  const activiteLink = activiteVentesLinkFromFilters(filters);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto fade-in" data-testid="historique-page">
        <PageHeader
          title={t('hist.title')}
          subtitle={t('historique.subtitle')}
          icon={History}
          testId="historique-header"
          actions={(
            <Link
              to={activiteLink}
              className="btn-secondary px-4 py-2 min-h-[44px] text-sm inline-flex items-center gap-2"
              data-testid="historique-link-activite"
            >
              <Activity className="w-4 h-4" />
              {t('historique.centreActivite')}
            </Link>
          )}
        />

        <div
          className="rounded-xl border p-4 sm:p-5 space-y-4"
          style={{ background: 'var(--rm-bg-elevated)', borderColor: 'var(--rm-border-subtle)' }}
          data-testid="historique-main-panel"
        >
          <HistoriqueSumUpPendingPanel
            payments={pendingPayments}
            onConfirm={handleConfirmSumupPayment}
            onCancel={handleCancelSumupPayment}
            testIdPrefix="historique"
          />

          {/* Filtres — collapsible mobile */}
          <div data-testid="historique-filters-section">
            <button
              type="button"
              onClick={() => setFiltersOpen((v) => !v)}
              className="lg:hidden w-full flex items-center justify-between gap-2 px-3 py-3 min-h-[44px] rounded-xl border mb-3"
              style={{ borderColor: 'var(--rm-border-subtle)', background: 'var(--rm-bg-panel)' }}
              data-testid="historique-filters-toggle"
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-[var(--rm-gray-100)]">
                <Filter className="w-4 h-4" /> {t('historique.filtresRecherche')}
              </span>
              {filtersOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            <div className={`${filtersOpen ? 'block' : 'hidden'} lg:block space-y-3`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
                <div className="sm:col-span-2 lg:col-span-2">
                  <label className="block text-xs text-[var(--rm-gray-500)] mb-1">{t('historique.rechercher')}</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--rm-gray-500)]" />
                    <input
                      type="text"
                      value={filters.search}
                      onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                      onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
                      className="input-dark w-full py-3 pl-9 text-sm min-h-[44px]"
                      placeholder={t('historique.searchPlaceholder')}
                      data-testid="historique-filter-search"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-[var(--rm-gray-500)] mb-1">{t('historique.vendeur')}</label>
                  <input
                    type="text"
                    value={filters.vendeur}
                    onChange={(e) => setFilters({ ...filters, vendeur: e.target.value })}
                    className="input-dark w-full py-3 text-sm min-h-[44px]"
                    placeholder={t('historique.tous')}
                    data-testid="historique-filter-vendeur"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[var(--rm-gray-500)] mb-1">{t('historique.dateDebut')}</label>
                  <input
                    type="date"
                    value={filters.date_debut}
                    onChange={(e) => setFilters({ ...filters, date_debut: e.target.value })}
                    className="input-dark w-full py-3 text-sm min-h-[44px]"
                    data-testid="historique-filter-date-debut"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[var(--rm-gray-500)] mb-1">{t('historique.dateFin')}</label>
                  <input
                    type="date"
                    value={filters.date_fin}
                    onChange={(e) => setFilters({ ...filters, date_fin: e.target.value })}
                    className="input-dark w-full py-3 text-sm min-h-[44px]"
                    data-testid="historique-filter-date-fin"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[var(--rm-gray-500)] mb-1">{t('historique.magasin')}</label>
                  <select
                    value={filters.magasin_id}
                    onChange={(e) => setFilters({ ...filters, magasin_id: e.target.value })}
                    className="input-dark w-full py-3 text-sm min-h-[44px]"
                    data-testid="historique-filter-magasin"
                  >
                    <option value="">{t('historique.tous')}</option>
                    <option value="M1">M1</option>
                    <option value="M2">M2</option>
                  </select>
                </div>
                <div className="flex gap-2 sm:col-span-2 lg:col-span-6 lg:justify-end">
                  <button
                    type="button"
                    onClick={applyFilters}
                    className="btn-accent flex-1 lg:flex-none px-5 py-3 min-h-[44px] text-sm"
                    data-testid="historique-filter-apply"
                  >
                    {t('historique.filtrer')}
                  </button>
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="px-4 py-3 min-h-[44px] rounded-xl border text-sm text-[var(--rm-gray-300)] hover:bg-white/5"
                    style={{ borderColor: 'var(--rm-border-subtle)' }}
                    data-testid="historique-filter-reset"
                  >
                    {t('historique.reinitialiser')}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <HistoriqueVentesActionsBar
            testIdPrefix="historique"
            onRetrait={() => modals.setShowRetraitModal(true)}
            onRapportVendeur={handleRapportVendeur}
          />

          <HistoriqueVentesList
            ventes={ventes}
            loading={loading}
            isAdmin={isAdmin}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            convertingRef={convertingRef}
            actionHandlers={actionHandlers}
            testIdPrefix="historique"
          />

          {/* Totaux — Carlos uniquement (SEC-01) */}
          {ventes.length > 0 && isCarlosOnly && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 border-t pt-4 mt-2 text-sm" style={{ borderColor: 'var(--rm-border-subtle)' }} data-testid="historique-totaux">
              <div className="rounded-xl border p-3" style={{ borderColor: 'var(--rm-border-subtle)', background: 'var(--rm-bg-panel)' }}>
                <div className="text-xs text-[var(--rm-gray-500)]">{t('historique.totalVentes')}</div>
                <div className="font-bold text-[var(--rm-success)]">{formatCurrency(totaux.ventes)}</div>
              </div>
              <div className="rounded-xl border p-3" style={{ borderColor: 'var(--rm-border-subtle)', background: 'var(--rm-bg-panel)' }}>
                <div className="text-xs text-[var(--rm-gray-500)]">{t('historique.totalAcomptes')}</div>
                <div className="font-bold text-[var(--rm-success)]">{formatCurrency(totaux.acomptes)}</div>
              </div>
              <div className="rounded-xl border p-3" style={{ borderColor: 'var(--rm-border-subtle)', background: 'var(--rm-bg-panel)' }}>
                <div className="text-xs text-[var(--rm-gray-500)]">{t('historique.totalRetraits')}</div>
                <div className="font-bold text-[var(--rm-red-light)]">{formatCurrency(totaux.retraits)}</div>
              </div>
              <div className="rounded-xl border p-3" style={{ borderColor: 'var(--rm-border-subtle)', background: 'var(--rm-bg-panel)' }}>
                <div className="text-xs text-[var(--rm-gray-500)]">{t('historique.totalGlobal')}</div>
                <div className="font-bold text-[var(--rm-white)]">{formatCurrency(totaux.global)}</div>
              </div>
            </div>
          )}

          {commissions && isAdmin && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 border-t pt-4 mt-2 text-sm" style={{ borderColor: 'var(--rm-border-subtle)' }} data-testid="historique-commissions">
              <div className="rounded-xl border p-3" style={{ borderColor: 'var(--rm-border-subtle)', background: 'var(--rm-bg-panel)' }}>
                <div className="text-xs text-[var(--rm-gray-500)]">{t('historique.commVentes', { pct: 15 })}</div>
                <div className="font-bold text-[var(--rm-success)]">{formatCurrency(commissions.commission_vente)}</div>
              </div>
              <div className="rounded-xl border p-3" style={{ borderColor: 'var(--rm-border-subtle)', background: 'var(--rm-bg-panel)' }}>
                <div className="text-xs text-[var(--rm-gray-500)]">{t('historique.commReparations', { pct: 20 })}</div>
                <div className="font-bold text-[var(--rm-gray-200)]">{formatCurrency(commissions.commission_reparation)}</div>
              </div>
              <div className="rounded-xl border p-3" style={{ borderColor: 'var(--rm-border-subtle)', background: 'var(--rm-bg-panel)' }}>
                <div className="text-xs text-[var(--rm-gray-500)]">{t('historique.commCommandes', { pct: 20 })}</div>
                <div className="font-bold text-[var(--rm-gray-200)]">{formatCurrency(commissions.commission_commande)}</div>
              </div>
              <div className="rounded-xl border p-3" style={{ borderColor: 'var(--rm-border-subtle)', background: 'var(--rm-bg-panel)' }}>
                <div className="text-xs text-[var(--rm-gray-500)]">{t('historique.totalCommissions')}</div>
                <div className="font-bold text-[var(--rm-white)]">{formatCurrency(commissions.total_commission)}</div>
              </div>
            </div>
          )}

        </div>
      </div>

      <HistoriqueVenteModals api={api} modals={modals} />

    </Layout>
  );
};

export default HistoriquePage;
